import type Player from "@vimeo/player";

// Confirm playback commands against player state. Vimeo can omit a method
// acknowledgement when a seek is interrupted, leaving its SDK promise pending.
export function createVimeoTrimTransport(player: Player, duration: () => number) {
  let cancel: (() => void) | undefined;
  let disposed = false;
  const run = (command: () => Promise<unknown>, complete: () => Promise<boolean>) => {
    if (disposed) return Promise.resolve();
    cancel?.();
    return new Promise<void>((resolve, reject) => {
      let finished = false;
      let tick: ReturnType<typeof setTimeout> | undefined;
      const finish = (error?: Error) => {
        if (finished) return;
        finished = true;
        clearTimeout(tick);
        clearTimeout(timeout);
        if (cancel === abort) cancel = undefined;
        if (error) reject(error);
        else resolve();
      };
      const abort = () => finish(new Error("Playback action replaced."));
      cancel = abort;
      const timeout = setTimeout(() => finish(new Error("Vimeo playback did not respond. Please try again.")), 10000);
      const check = async () => {
        try {
          if (await complete()) return finish();
        } catch {
          // Retry a transient status-read failure until the deadline.
        }
        if (!finished) tick = setTimeout(() => void check(), 75);
      };
      void command().catch(error => finish(error instanceof Error ? error : new Error(String(error))));
      tick = setTimeout(() => void check(), 25);
    });
  };
  return {
    currentTime: () => player.getCurrentTime(),
    pause: () => run(() => player.pause(), () => player.getPaused()),
    play: () => run(() => player.play(), async () =>
      !(await player.getPaused()) || (duration() > 0 && await player.getCurrentTime() >= duration() - 0.05)),
    seek: (seconds: number) => {
      const target = duration() > 0 ? Math.min(seconds, Math.max(0, duration() - 1 / 30)) : seconds;
      return run(() => player.setCurrentTime(target), async () =>
        Math.abs(await player.getCurrentTime() - target) < 0.08 && !(await player.getSeeking()));
    },
    dispose: () => { disposed = true; cancel?.(); },
  };
}
